'use client'

import { produce } from 'immer'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { Fragment, memo, useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { IOpeningHour } from '@/api/restaurants/types/restaurant-types'
import { DropdownMenu } from '@/components/dropdown-menu'
import { Skeleton } from '@/components/skeleton'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailOpeningHoursList } from '@/features/restaurants/restaurant-detail/restaurant-detail-opening-hours/restaurant-detail-opening-hours-list'
import { parseDayOfWeek } from '@/features/restaurants/utils/parse-day-of-week'
import { parseReadableDayOpeningHours } from '@/features/restaurants/utils/parse-readable-day-opening-hours'
import { SvgFilledChevronRight } from '@/icons/svg-filled-chevron-right'
import { SvgOutlinedSchedule } from '@/icons/svg-outlined-schedule'
import { noOp } from '@/utils/functions/noOp'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IRestaurantDetailOpeningHoursProps {
    styleType: 'collapsible' | 'static'
}

export const RestaurantDetailOpeningHours = memo(
    ({ styleType }: IRestaurantDetailOpeningHoursProps) => {
        const t = useTranslations()

        // Used on desktop
        const [isDropdownOpen, setIsDropdownOpen] = useState(false)

        const { restaurant } = useRestaurantDetailContext()

        const groupedOpeningHours = useMemo(() => {
            const groupedByDay = restaurant?.openingHours?.reduce<IOpeningHour[][]>(
                (acc, openingHour) => {
                    const prevDayEntry = acc.length ? acc[acc.length - 1] : undefined

                    // Check if the current openingHour belongs to the previous day
                    // This is the case when a restaurant opens from e.g. 12:00 to 15:00 and then from 18:00 to 23:00
                    const belongsToPrevDayEntry =
                        prevDayEntry && prevDayEntry[0].day === openingHour.day

                    if (belongsToPrevDayEntry) {
                        return produce(acc, (draft) => {
                            draft[draft.length - 1].push(openingHour)
                        })
                    }

                    return [...acc, [openingHour]]
                },
                [] as IOpeningHour[][]
            )

            return groupedByDay
        }, [restaurant])

        const getDayOpeningHours = useCallback(
            (dayNumber: number) => {
                const day = parseDayOfWeek(dayNumber)

                if (!day || !groupedOpeningHours) return null

                return groupedOpeningHours.find((entry) => entry[0]?.day === day)
            },
            [groupedOpeningHours]
        )

        const todaysOpeningHours = useMemo(() => {
            const now = new Date()
            const dayNumber = now.getDay()

            return getDayOpeningHours(dayNumber)
        }, [getDayOpeningHours])

        const readableTodayOpeningHours = useMemo(() => {
            if (!todaysOpeningHours) return null

            return parseReadableDayOpeningHours(todaysOpeningHours, t)
        }, [todaysOpeningHours, t])

        const [forceRecalculation, setForceRecalculation] = useState(0)

        const explainedOpening = useMemo((): Record<'mobile' | 'desktop', string> | undefined => {
            if (typeof window === 'undefined') return

            noOp(`Force recalculation: ${forceRecalculation}`)

            const now = new Date()
            const nowHours = now.getHours()
            const nowMinutes = now.getMinutes()

            const nowTime = parseFloat(`${nowHours}.${nowMinutes}`)

            const currentOpeningHour = todaysOpeningHours?.find((openingHour) => {
                const { openingTime, closingTime } = openingHour

                const openingTimeFloat = parseFloat(openingTime)
                const closingTimeFloat = parseFloat(closingTime) || 24

                // Some opening hours go from 10:00 to 02:00. This means the place is open until 02:00 the next day
                const isOpenForLongerThanTheDay = closingTimeFloat < openingTimeFloat

                return (
                    nowTime >= openingTimeFloat &&
                    (nowTime <= closingTimeFloat || isOpenForLongerThanTheDay)
                )
            })

            const isMidnightClosing =
                currentOpeningHour?.closingTime === '00:00' ||
                currentOpeningHour?.closingTime === '24:00'

            if (currentOpeningHour) {
                const isClosingTimeTheNextDay =
                    !isMidnightClosing &&
                    parseFloat(currentOpeningHour.closingTime) <
                        parseFloat(currentOpeningHour.openingTime)

                // Turn 00:00 into 23:59
                const safeClosingTime = isMidnightClosing ? '23:59' : currentOpeningHour.closingTime

                const [closingHourStr, closingMinuteStr] = safeClosingTime.split(':')
                const closingHour = Number(closingHourStr)
                const closingMinute = Number(closingMinuteStr)

                const rawHoursUntilClosing = isClosingTimeTheNextDay
                    ? closingHour + (24 - nowHours)
                    : closingHour - nowHours

                const timeUntilClosing = rawHoursUntilClosing + (closingMinute - nowMinutes) / 60

                // round to half an hour
                const hoursUntilClosing = Math.round(timeUntilClosing * 2) / 2

                const readableTimeUntilClosing = run(() => {
                    if (hoursUntilClosing <= 1) {
                        const minutesUntilClosing = Math.floor(hoursUntilClosing * 60)

                        return `${minutesUntilClosing} ${t('restaurants.detail.openingHours.minutes')}`
                    }

                    if (hoursUntilClosing > 3) {
                        // Don't count half hours for more than 3 hours. No one cares if a place is open for 5.5 hours or 5 hours
                        return `${Math.floor(hoursUntilClosing)} ${t('restaurants.detail.openingHours.hours')}`
                    }

                    return `${hoursUntilClosing} ${t('restaurants.detail.openingHours.hours')}`
                })

                const readableShort = t('restaurants.detail.openingHours.openNowShort')

                const readableSpecific = t('restaurants.detail.openingHours.openNowSpecific', {
                    readableTimeUntilClosing,
                })

                return {
                    desktop: styleType === 'collapsible' ? readableShort : readableSpecific,
                    mobile: readableSpecific,
                }
            }

            const nextTodayOpeningHour = todaysOpeningHours?.find((openingHour) => {
                const { openingTime } = openingHour

                const openingTimeFloat = parseFloat(openingTime)

                return openingTimeFloat > nowTime
            })

            if (nextTodayOpeningHour) {
                const { openingTime } = nextTodayOpeningHour

                const [openingHourStr, openingMinuteStr] = openingTime.split(':')
                const openingHour = Number(openingHourStr)
                const openingMinute = Number(openingMinuteStr)

                const rawHoursUntilOpening = openingHour - nowHours
                const timeUntilOpening = rawHoursUntilOpening + (openingMinute - nowMinutes) / 60

                // round to half an hour
                const hoursUntilOpen = Math.round(timeUntilOpening * 2) / 2

                if (hoursUntilOpen === 0 || timeUntilOpening < 1) {
                    const nextOpeningHourHours = parseInt(openingTime.split(':')[0])
                    const isInNextFullHour = nextOpeningHourHours > nowHours

                    const minutesOnTheHour = openingTime.split(':')[1]

                    const minutesUntilOpen = run(() => {
                        if (isInNextFullHour) {
                            return 60 - nowMinutes + parseInt(minutesOnTheHour)
                        }

                        return parseInt(minutesOnTheHour) - nowMinutes
                    })

                    return {
                        desktop: t('restaurants.detail.openingHours.opensInMinutes', {
                            minutesUntilOpen,
                        }),
                        mobile: t('restaurants.detail.openingHours.opensInMinutes', {
                            minutesUntilOpen,
                        }),
                    }
                }

                return {
                    desktop: t('restaurants.detail.openingHours.opensInHours', {
                        hoursUntilOpen,
                    }),
                    mobile: t('restaurants.detail.openingHours.opensInHours', {
                        hoursUntilOpen,
                    }),
                }
            }

            return {
                desktop: t('restaurants.detail.openingHours.closed'),
                mobile: t('restaurants.detail.openingHours.closed'),
            }
        }, [todaysOpeningHours, t, styleType, forceRecalculation])

        // Recalculate the opening hours every minute
        useLayoutEffect(() => {
            const interval = setInterval(() => {
                setForceRecalculation((prev) => prev + 1)
            }, 1000 * 60)

            return () => clearInterval(interval)
        }, [])

        const [showExplanation, setShowExplanation] = useState(false)

        // Avoid hydration mismatch (as the content relies on the users local machine time)
        useLayoutEffect(() => {
            const timeout = setTimeout(() => {
                setShowExplanation(true)
            }, 0)

            return () => clearTimeout(timeout)
        }, [])

        const renderDropdownContent = useCallback(() => {
            return (
                <RestaurantDetailOpeningHoursList
                    getDayOpeningHours={getDayOpeningHours}
                    className="p-4 gap-x-10"
                />
            )
        }, [getDayOpeningHours])

        if (!restaurant) return null

        const innerNode = (
            <div
                className={cn(
                    'flex flex-row items-center relative cursor-pointer webkit-tap-transparent',
                    styleType === 'collapsible' && 'px-1'
                )}
            >
                <div className="flex flex-row items-center justify-start">
                    <motion.div
                        initial={{ opacity: showExplanation ? 1 : 0 }}
                        animate={{ opacity: showExplanation ? 1 : 0 }}
                        className={cn('hidden', styleType === 'collapsible' && 'block mr-2')}
                    >
                        <SvgOutlinedSchedule
                            color={COLORS.fg.primary}
                            size={19}
                            style={{ position: 'relative', bottom: '0.5px' }}
                        />
                    </motion.div>

                    <div className="flex flex-row items-center gap-x-2 md:gap-x-1.5 select-none">
                        <AnimatePresence initial mode="popLayout">
                            {run(() => {
                                if (!showExplanation)
                                    return (
                                        <motion.div
                                            initial={{ opacity: 0.5 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ opacity: { duration: 0 } }}
                                            key="opening-hours-skeleton"
                                        >
                                            <Skeleton
                                                className="w-40 h-6 bg-bg-neutral-surface-strong"
                                                baseOpacity={1}
                                            />
                                        </motion.div>
                                    )

                                if (!explainedOpening) return null

                                return (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ opacity: { duration: 0.5, ease: 'easeOut' } }}
                                        key="opening-hours-explanation"
                                    >
                                        <p className="hidden md:block text-label-lg whitespace-nowrap">
                                            {explainedOpening.desktop}
                                        </p>

                                        <p className="block md:hidden text-label-lg whitespace-nowrap">
                                            {explainedOpening.mobile}
                                        </p>
                                    </motion.div>
                                )
                            })}
                        </AnimatePresence>

                        {Boolean(readableTodayOpeningHours) && (
                            <p
                                className={cn(
                                    'hidden text-p-md whitespace-nowrap',
                                    styleType === 'collapsible' && 'md:block'
                                )}
                            >
                                {t('restaurants.detail.openingHours.time', {
                                    time: String(readableTodayOpeningHours),
                                })}
                            </p>
                        )}
                    </div>
                </div>

                <motion.div
                    initial={{ rotate: isDropdownOpen ? 270 : 90 }}
                    animate={{ rotate: isDropdownOpen ? 270 : 90 }}
                    className={cn('hidden', styleType === 'collapsible' && 'block')}
                >
                    <SvgFilledChevronRight color={COLORS.fg.primary} size={24} />
                </motion.div>
            </div>
        )

        return (
            <Fragment>
                <div className={cn('hidden', styleType === 'static' && 'block w-fit')}>
                    {innerNode}

                    <div className="mt-4" />

                    <RestaurantDetailOpeningHoursList
                        getDayOpeningHours={getDayOpeningHours}
                        className="gap-x-16"
                    />
                </div>

                <div
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    className={cn('hidden relative', styleType === 'collapsible' && 'block')}
                >
                    {innerNode}

                    {groupedOpeningHours && (
                        <DropdownMenu
                            align="right"
                            isOpen={isDropdownOpen}
                            setIsOpen={setIsDropdownOpen}
                            className="top-9 cursor-default min-w-fit"
                            renderContent={renderDropdownContent}
                        />
                    )}
                </div>
            </Fragment>
        )
    }
)

RestaurantDetailOpeningHours.displayName = 'RestaurantDetailOpeningHours'
